/**
 * ArchiveLeaseModal Component
 * Modal for archiving a lease with optional actual end date
 */

import { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.css';
import type { LeaseListItem, ArchiveLeaseInput } from '../types/lease';
import { formatCalendarDate, asCalendarDateString } from '@resida/shared';

interface ArchiveLeaseModalProps {
  lease: LeaseListItem;
  onConfirm: (data: ArchiveLeaseInput) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  onClearError?: () => void;
}

export function ArchiveLeaseModal({
  lease,
  onConfirm,
  onCancel,
  isSubmitting = false,
  error = null,
  onClearError,
}: ArchiveLeaseModalProps) {
  const [actualEndDate, setActualEndDate] = useState<string>('');

  const parseDateStr = (dateStr: string): Date | undefined => {
    if (!dateStr) return undefined;
    const parts = dateStr.split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onConfirm({
      actualEndDate: actualEndDate || null,
    });
  };


  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
      onClick={onCancel}
    >
      <div
        className="bg-card rounded-lg w-[90%] max-w-lg max-h-[90vh] overflow-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-6 py-5 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground m-0">Archive Lease</h3>
          <button
            onClick={onCancel}
            className="bg-transparent border-none text-[28px] leading-none text-muted-foreground cursor-pointer p-0 w-8 h-8 flex items-center justify-center rounded hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60 min-h-[44px] min-w-[44px]"
            disabled={isSubmitting}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="p-6 flex flex-col gap-5">
            {error && (
              <div className="flex items-start justify-between gap-3 px-4 py-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md">
                <div className="flex items-start gap-3 flex-1">
                  <span className="text-lg flex-shrink-0 mt-0.5">⚠️</span>
                  <div className="text-sm text-destructive leading-[1.4]">{error}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onClearError?.()}
                  className="bg-transparent border-none text-lg text-muted-foreground cursor-pointer p-0 w-5 h-5 flex items-center justify-center flex-shrink-0 rounded hover:bg-accent"
                  aria-label="Dismiss error"
                >
                  ✕
                </button>
              </div>
            )}

            <p className="text-sm text-muted-foreground m-0">
              Are you sure you want to archive this lease?
            </p>

            <div className="bg-accent/50 rounded-md p-3 flex flex-col gap-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-muted-foreground">Start Date:</span>
                <span className="text-foreground">{formatCalendarDate(asCalendarDateString(lease.startDate))}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium text-muted-foreground">Scheduled End:</span>
                <span className="text-foreground">{formatCalendarDate(lease.endDate == null ? lease.endDate : asCalendarDateString(lease.endDate))}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium text-muted-foreground">Tenants:</span>
                <span className="text-foreground">
                  {lease.tenants.map(t => `${t.firstName} ${t.lastName}`).join(', ')}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="actualEndDate" className="text-sm font-medium text-muted-foreground">
                Actual End Date (optional)
              </label>
              <DatePicker
                id="actualEndDate"
                selected={parseDateStr(actualEndDate) ?? null}
                onChange={(date: Date | null) => {
                  const dateStr = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setActualEndDate(dateStr);
                }}
                dateFormat="MM/dd/yyyy"
                placeholderText="Select actual end date"
                className="date-picker"
                disabled={isSubmitting}
                maxDate={new Date()}
              />
              <p className="text-xs text-muted-foreground m-0">
                If left blank, the current date will be used
              </p>
            </div>

            <div
              data-testid="archive-side-effects-info"
              className="rounded-md bg-muted/50 border border-border p-4"
            >
              <p className="text-sm font-medium text-foreground mb-2">Archiving this lease will:</p>
              <ul className="flex flex-col gap-1 mb-2">
                <li className="text-sm text-muted-foreground">• Mark all current tenants as departed</li>
                <li className="text-sm text-muted-foreground">• Cancel all active recurring payment schedules</li>
                <li className="text-sm text-muted-foreground">• Stop automatic charges, late fees and payment collection</li>
              </ul>
              <p className="text-xs text-muted-foreground m-0">
                The lease will become read-only: no charges, credits or payments can be recorded
                once it&apos;s archived. Settle any outstanding balance first if you can.
              </p>
            </div>

            <div className="flex gap-3 p-3 bg-warning/10 border border-warning/20 rounded-md">
              <span className="text-xl flex-shrink-0">⚠️</span>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-warning m-0">Recurring payments won&apos;t restart</p>
                <p className="text-[13px] text-warning m-0">
                  You can un-archive this lease later, but it comes back as ended and its
                  recurring payment schedules stay cancelled. The unit will become available for
                  new leases.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md cursor-pointer hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 text-sm font-medium text-destructive-foreground bg-destructive border-none rounded-md cursor-pointer hover:bg-destructive/90 disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Archiving...' : 'Archive Lease'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
